import type { ArealplanDelområde } from '@origo/arealplan';
import type { Område } from '@origo/seksjon';
import type { SeksjonContext } from '../admin/types';
import type { DelområdegruppeView } from '../admin/components/NavPanel';
import { findFeaturesForContext } from '../admin/findFeaturesForContext';
import { mergePolygonsUnionOperator } from '../planer/utils/geometryUtils';

/**
 * Hierarkinivåene en eiendomsvelger kan knyttes til. Verdien brukes både som
 * `omradetype`-felt i `eiendommer_v4/v7`-laget (persistering pr. nivå) og til å
 * velge grunnsett-strategi: geometri‑intersect for nivåer med avgrensning,
 * ren persistert (manuelt) for egendefinerte områder uten egen geometri.
 */
export type EiendomsvelgerOmradetype =
    | 'plan'
    | 'delomrade'
    | 'delomradegruppe'
    | 'egendefinert';

export interface EiendomsvelgerTarget {
    /** Stabil koblingsnøkkel — se SelectedPlanGeometryPayload-historikken / NaboListeService. */
    omradeId: string;
    omradetype: EiendomsvelgerOmradetype;
    /**
     * Geometri for intersect‑basert grunnsett (plan/delområde/gruppe), eller
     * `null` for manuelt‑bare nivåer (egendefinert), som lastes fra persisterte
     * add‑deltaer uten geometri.
     *
     * Bæres som geometri‑INSTANS (ikke JSON): kilde‑geometrien fra react‑query er
     * referanse‑stabil mellom refetches, mens en `.toJSON()`‑kopi ble et nytt
     * objekt hver render → `feature`‑memoet i Eiendommer rekonstruerte
     * NaboListeService midt i økten (og `activeOmradeId` ble transient undefined).
     * NaboListeService/computeOverlappingTeiger tar uansett både instans og JSON.
     */
    geometry: unknown | null;
}

/**
 * Bygger eiendomsvelger-målet for en navigasjonskontekst. Ren funksjon — all
 * geometri sendes inn (plangeometrien hentes av kalleren via
 * `useArealplanGeometry`). Returnerer `null` for nivåer uten eiendomsvelger
 * (delområdetype/kommune/admin-visninger) eller når avgrensningen mangler.
 *
 * Sentraliserer feature‑konstruksjonen som tidligere var spredt i AppLayout,
 * AdminPage og OmrådeEditorPanel.
 */
export function buildEiendomsvelgerTarget(args: {
    context: SeksjonContext;
    planId: string;
    /** Plangeometri (RpOmråde) fra useArealplanGeometry(plan). Polygon eller JSON. */
    planGeometryJson: unknown | null;
    planomrader: ArealplanDelområde[];
    egendefinerte: Område[];
    gruppeViews: readonly DelområdegruppeView[];
}): EiendomsvelgerTarget | null {
    const { context, planId, planGeometryJson, planomrader, egendefinerte, gruppeViews } = args;

    switch (context.type) {
        case 'plan': {
            // Bær geometri-instansen direkte (ingen toJSON) — referanse-stabil fra
            // react-query, så target/feature ikke rekonstruerer tjenesten per render.
            if (!planGeometryJson) return null;
            return { omradeId: planId, omradetype: 'plan', geometry: planGeometryJson };
        }
        case 'delomrade': {
            const feature = findFeaturesForContext(context, planomrader)[0];
            if (!feature?.geometri || feature.omradeId == null) return null;
            return {
                omradeId: feature.omradeId,
                omradetype: 'delomrade',
                geometry: feature.geometri,
            };
        }
        case 'delomradegruppe': {
            if (!context.gruppeId) return null;
            const gruppe = gruppeViews.find((g) => g.id === context.gruppeId);
            if (!gruppe) return null;
            const members = new Set(gruppe.memberDelomradeIds);
            const features = findFeaturesForContext(context, planomrader, members);
            const union = mergePolygonsUnionOperator(features);
            if (!union) return null;
            return {
                omradeId: gruppe.id,
                omradetype: 'delomradegruppe',
                geometry: union,
            };
        }
        case 'omrade': {
            const omrade = egendefinerte.find((o) => o.id === context.omradeId);
            if (!omrade) return null;
            return { omradeId: String(omrade.id), omradetype: 'egendefinert', geometry: null };
        }
        default:
            return null;
    }
}
